import { IPhonePlaceholder } from './Placeholders';
import appStoreBadge from '../assets/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg';
import { trackAppStoreClick } from '../analytics';
import imgCalendar from '../assets/iphone-analytics-workout-calendar-dark.webp';
import imgActiveSession from '../assets/iphone-workout-open-session-chest-fly.webp';
import imgProgress from '../assets/iphone-analytics-exercise-sets-chart.webp';

export default function Hero() {
  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-16 bg-gray-50">
      {/* Background gradient blobs – hidden on mobile to avoid iOS GPU cost */}
      <div className="absolute inset-0 pointer-events-none hidden sm:block">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-orange/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-brand-section/8 rounded-full blur-[100px]" />
        <div className="absolute top-10 right-10 w-64 h-64 bg-blue-400/5 rounded-full blur-[80px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20 lg:py-28">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Text content */}
          <div className="flex-1 text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-brand-orange/15 border border-brand-orange/30 rounded-full px-4 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-orange animate-pulse" />
              <span className="text-brand-orange text-sm font-medium">100% Free · No Subscriptions</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.05] tracking-tight mb-6">
              No More{' '}
              <span className="text-brand-orange">
                Waiting
              </span>
              <br />– It's Here!
            </h1>

            <p className="text-gray-600 text-lg sm:text-xl max-w-xl mx-auto lg:mx-0 mb-4 leading-relaxed">
              The weight log every bodybuilder and powerlifter's been dying for — 100% free, no cap.
            </p>
            <p className="text-gray-500 text-base max-w-xl mx-auto lg:mx-0 mb-2 leading-relaxed">
              Track workouts, crush PRs, and stay accountable on your iPhone &amp; Apple Watch.
            </p>

            <p className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-6 mt-6">
              Your Gains Await – Available on the App Store
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4">
              <a
                href="https://apps.apple.com/us/app/gymbud-pro/id6478331872"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAppStoreClick('hero')}
                className="inline-block hover:opacity-90 hover:scale-105 transform transition-all duration-200"
              >
                <img src={appStoreBadge} alt="Download on the App Store" className="h-14 w-auto" />
              </a>

              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Free forever · No ads
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-8 mt-12 justify-center lg:justify-start">
              {[
                { value: '100%', label: 'Free' },
                { value: 'iPhone', label: '& Apple Watch' },
                { value: '∞', label: 'PR Tracking' },
              ].map((stat) => (
                <div key={stat.label} className="text-center lg:text-left">
                  <div className="text-2xl font-black text-gray-900">{stat.value}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── iPhone placeholders ── */}
          <div className="flex-shrink-0 relative flex items-end gap-3 justify-center">
            {/* Glow */}
            <div className="absolute inset-0 bg-brand-orange/20 blur-3xl rounded-full scale-75 pointer-events-none hidden sm:block" />

            {/* Left phone – rotated, muted */}
            <div className="hidden sm:block opacity-60" style={{ transform: 'rotate(-6deg) translateY(20px)' }}>
              <IPhonePlaceholder label="Calendar View" width={190} height={390} src={imgCalendar} loading="eager" />
            </div>

            {/* Center phone – spotlight */}
            <div className="relative z-10">
              <div className="absolute -inset-4 bg-brand-orange/15 blur-2xl rounded-full pointer-events-none hidden sm:block" />
              <IPhonePlaceholder label="Active Workout View" width={220} height={450} src={imgActiveSession} loading="eager" fetchpriority="high" />
            </div>

            {/* Right phone – rotated, muted */}
            <div className="hidden sm:block opacity-60" style={{ transform: 'rotate(6deg) translateY(20px)' }}>
              <IPhonePlaceholder label="Progress Graphs" width={190} height={390} src={imgProgress} loading="eager" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 animate-bounce">
        <span className="text-gray-400 text-xs tracking-widest uppercase">Scroll</span>
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
}
